
import { useState } from 'react';
import { toast } from 'sonner';
import { useFetchUsers } from './useFetchUsers';
import { useCreateUser } from './useCreateUser';
import { useUpdateUser } from './useUpdateUser';
import { useDeleteUser } from './useDeleteUser';
import { useVerifyUser } from './useVerifyUser';
import { useCountries } from './useCountriesAndConstituencies';
import { usePagination } from '@/hooks/use-pagination';
import { FormData } from './types';
import { UserFilters, UserSortOption } from './UserFilters';
import type { Database } from "@/integrations/supabase/types";

type UserProfile = Database['public']['Tables']['profiles']['Row'];

export const useUserManagement = () => {
  // Pagination
  const pagination = usePagination({ 
    itemsPerPage: 25, 
    storageKey: 'admin-users-pagination'
  });

  // State for user management
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState<UserFilters>({
    role: 'all',
    verified: 'all',
    country: 'all',
    verification: 'all'
  });
  const [sortOption, setSortOption] = useState<UserSortOption>('newest');

  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [countryAssignmentOpen, setCountryAssignmentOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    role: 'public',
    verified: false,
    country_code: '',
    constituency_code: '',
    phone_number: '',
    demographics: null
  });

  // Fetch users with filters and pagination
  const { 
    paginatedUsers, 
    totalUsers, 
    loading, 
    refetch: refetchUsers 
  } = useFetchUsers({ 
    searchQuery, 
    filters, 
    sortOption, 
    pagination 
  });

  // CRUD operations
  const { createUser } = useCreateUser(refetchUsers);
  const { updateUser } = useUpdateUser(refetchUsers);
  const { deleteUser, isDeleting } = useDeleteUser(refetchUsers);
  const { verifyUser } = useVerifyUser(refetchUsers);

  // Get countries and constituencies
  const { countries } = useCountries();

  // User action handlers
  const handleEditUser = (user: UserProfile) => {
    setSelectedUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      role: user.role,
      verified: user.verified || false,
      country_code: user.country_code || '',
      constituency_code: user.constituency_code || '',
      phone_number: user.phone_number || '',
      demographics: user.demographics as any
    });
    setEditModalOpen(true);
  };

  const handleVerifyUser = (user: UserProfile) => {
    setSelectedUser(user);
    setVerifyDialogOpen(true);
  };

  const handleManageCountries = (user: UserProfile) => {
    setSelectedUser(user);
    setCountryAssignmentOpen(true);
  };

  const handleCreateUser = () => {
    setFormData({
      name: '',
      email: '',
      role: 'public',
      verified: false,
      country_code: '',
      constituency_code: '',
      phone_number: '',
      demographics: null
    });
    setCreateModalOpen(true);
  };

  const handleDeleteUser = async (user: UserProfile) => {
    try {
      const success = await deleteUser(user);
      if (success) {
        toast.success('User deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    try {
      await updateUser(selectedUser.id, formData);
      setEditModalOpen(false);
      setSelectedUser(null);
      toast.success('User updated successfully');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleCreateUserSubmit = async () => {
    try {
      await createUser(formData);
      setCreateModalOpen(false);
      toast.success('User created successfully');
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    }
  };

  const confirmVerifyUser = async () => {
    if (!selectedUser) return;
    
    try {
      await verifyUser(selectedUser.id);
      setVerifyDialogOpen(false);
      setSelectedUser(null);
      toast.success('User verified successfully');
    } catch (error) {
      console.error('Error verifying user:', error);
      toast.error('Failed to verify user');
    }
  };

  return {
    // Data
    paginatedUsers,
    totalUsers,
    loading,
    
    // Pagination
    pagination,
    
    // Filters and search
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    sortOption,
    setSortOption,
    
    // Modal states
    editModalOpen,
    setEditModalOpen,
    verifyDialogOpen,
    setVerifyDialogOpen,
    createModalOpen,
    setCreateModalOpen,
    countryAssignmentOpen,
    setCountryAssignmentOpen,
    selectedUser,
    formData,
    setFormData,
    
    // Actions
    handleEditUser,
    handleVerifyUser,
    handleManageCountries,
    confirmVerifyUser,
    handleUpdateUser,
    handleCreateUser,
    handleCreateUserSubmit,
    handleDeleteUser,
    
    // States
    isDeleting,
    
    // Data
    countries
  };
};
